import { GPSTrackDTO, GPSTrackMapper } from '@nx-smartmonkey/shared/domain';
import { useEffect, useRef } from 'react';
// import _ from 'lodash';

import _ from 'lodash';
import { webSocketsService } from '../../application/WebSocketsService';
import { PlanActionType } from '../../context/plan/actions';
import { usePlanContext } from '../../context/plan/context';
import { DriverDTO } from '../../domain/driver/DriverDTO';
import { PlanFlatDTO } from '../../domain/plan/PlanFlatDTO';
import { StopDTO } from '../../domain/stop/StopDTO';
import { DriverMapper } from '../../mappers/driver/DriverMapper';
import { PlanFlatMapper } from '../../mappers/plan/PlanFlatMapper';
import { StopMapper } from '../../mappers/stop/StopMapper';
import { useRetrieveDriver } from '../driver/useRetrieveDriver';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../right-panel/useRetrieveRightPanelInfo';
import { useRetrieveStop } from '../stop/useRetrieveStop';
import { useRetrieveActivePlan } from './useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from './useRetrieveActivePlanDrivers';

const getSubscriptionPlanPath = (activePlanId: string) => `/plan/${activePlanId}`;

// Hooks act as 'Adapter' layer.
export const useActivePlanWebSockets = () => {
  const { planDispatch } = usePlanContext();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan, run: refetchActiveFlatPlan } = useRetrieveActivePlan();
  const { run: refetchDrivers } = useRetrieveActivePlanDrivers();
  const { run: refetchStop } = useRetrieveStop({ showErrorMessage: false });
  const { run: refetchDriver } = useRetrieveDriver({ showErrorMessage: false });
  const { detailId, detailType, detailTab, cleanRightPanelInfo } = useRetrieveRightPanelInfo();

  const debouncedRefetchDrivers = useRef(
    _.debounce(() => {
      refetchDrivers();
    }, 500)
  ); // 0.5 seconds in milliseconds

  const debouncedRefetchPlanFlat = useRef(
    _.debounce(() => {
      refetchActiveFlatPlan();
    }, 500)
  ); // 0.5 seconds in milliseconds

  useEffect(() => {
    if (!activePlan || webSocketsService.getSubscription(getSubscriptionPlanPath(activePlan.id))) {
      return;
    }

    webSocketsService.subscribe(getSubscriptionPlanPath(activePlan.id), (pubResponse) => {
      switch (pubResponse.id) {
        case `smartmonkey.planner.2.optimizer.optimization.started`:
        case `smartmonkey.planner.2.optimizer.optimization.cancelled`:
        case `smartmonkey.planner.2.optimizer.optimization.failed`:
        case `smartmonkey.planner.2.optimizer.optimization.succeeded`: {
          planDispatch({
            type: PlanActionType.SET_ACTIVE_PLAN_OPTIMIZATION_DATA,
            payload: {
              status: pubResponse.data.status,
              startedAt: pubResponse.data.created_at,
            },
          });
          break;
        }
        case `smartmonkey.planner.2.planner.plan.updated`: {
          const updatedActivePlan = PlanFlatMapper.toDomain(pubResponse.data as PlanFlatDTO);
          planDispatch({
            type: PlanActionType.SET_ACTIVE_PLAN,
            payload: {
              activePlan: updatedActivePlan,
            },
          });
          break;
        }
        case `smartmonkey.planner.2.planner.driver.created`: {
          const createdDriver = DriverMapper.toDomain(pubResponse.data as DriverDTO, activeProject!.custom_fields!);
          planDispatch({
            type: PlanActionType.CREATE_PLAN_DRIVER,
            payload: {
              driver: createdDriver,
            },
          });
          break;
        }
        case `smartmonkey.planner.2.planner.driver.updated`: {
          const updatedDriver = DriverMapper.toDomain(pubResponse.data as DriverDTO, activeProject!.custom_fields!);
          planDispatch({
            type: PlanActionType.UPDATE_PLAN_DRIVER,
            payload: {
              driver: updatedDriver,
            },
          });

          // Driver is opened at right panel so refetch the driver data.
          if (detailId === updatedDriver.id && detailType === `driver`) {
            refetchDriver();
          }
          break;
        }
        case `smartmonkey.planner.2.planner.driver.deleted`: {
          const deletedDriver = DriverMapper.toDomain(pubResponse.data as DriverDTO, activeProject!.custom_fields!);
          planDispatch({
            type: PlanActionType.DELETE_PLAN_DRIVER,
            payload: {
              driver: deletedDriver,
            },
          });

          // Driver is opened at right panel so clean the right panel.
          if (detailId === deletedDriver.id && detailType === `driver`) {
            cleanRightPanelInfo();
          }
          break;
        }
        case `smartmonkey.planner.2.drivers.stop.created`:
        case `smartmonkey.planner.2.planner.stop.created`: {
          const createdStop = StopMapper.toDomain(pubResponse.data as StopDTO, activeProject!.custom_fields!);
          planDispatch({
            type: PlanActionType.CREATE_PLAN_STOP,
            payload: {
              stop: createdStop,
            },
          });

          if (createdStop.driver_id) {
            refetchDrivers();
          }

          // Stop is opened at right panel so refetch the driver data.
          if (detailId === createdStop.driver_id && detailType === `driver`) {
            refetchDriver();
          }
          debouncedRefetchPlanFlat.current();

          break;
        }
        case `smartmonkey.planner.2.drivers.stop.updated`:
        case `smartmonkey.planner.2.planner.stop.reported`:
        case `smartmonkey.planner.2.planner.stop.updated`: {
          const updatedStop = StopMapper.toDomain(pubResponse.data as StopDTO, activeProject!.custom_fields!);
          planDispatch({
            type: PlanActionType.UPDATE_PLAN_STOP,
            payload: {
              stop: updatedStop,
            },
          });

          refetchDrivers();

          // Stop is opened at right panel so refetch the stop data.
          if (detailId === updatedStop.id && detailType === `stop`) {
            refetchStop();
          }

          // Stop is opened at right panel so refetch the driver data.
          if (detailId === updatedStop.driver_id && detailType === `driver`) {
            refetchDriver();
          }
          break;
        }
        case `smartmonkey.planner.2.drivers.stop.deleted`:
        case `smartmonkey.planner.2.planner.stop.deleted`: {
          const deletedStop = StopMapper.toDomain(pubResponse.data as StopDTO, activeProject!.custom_fields!);
          planDispatch({
            type: PlanActionType.DELETE_PLAN_STOP,
            payload: {
              stop: deletedStop,
            },
          });

          // Stop is opened at right panel so clean the right panel.
          if (detailId === deletedStop.id && detailType === `stop`) {
            cleanRightPanelInfo();
          }

          if (deletedStop.driver_id) {
            refetchDrivers();
          }

          // Stop is opened at right panel so refetch the driver data.
          if (detailId === deletedStop.driver_id && detailType === `driver`) {
            refetchDriver();
          }

          debouncedRefetchPlanFlat.current();
          break;
        }
        case `smartmonkey.planner.2.drivers.stop.rescheduled`:
        case `smartmonkey.planner.2.planner.stop.rescheduled`: {
          const updatedStop = StopMapper.toDomain(pubResponse.data as StopDTO, activeProject!.custom_fields!);
          planDispatch({
            type: PlanActionType.PLAN_STOP_RESCHEDULED,
            payload: {
              stop: updatedStop,
            },
          });

          // Stop is opened at right panel, so refetch the stop data.
          if (detailId === updatedStop.id && detailType === `stop`) {
            refetchStop();
          }

          if (pubResponse.id === `smartmonkey.planner.2.drivers.stop.rescheduled`) {
            debouncedRefetchDrivers.current();
          }
          break;
        }
        case `smartmonkey.planner.2.drivers.route.started`:
        case `smartmonkey.planner.2.drivers.route.finished`:
        case `smartmonkey.planner.2.drivers.route.updated`: {
          const driverId = (pubResponse.data as any).id;
          const status = (pubResponse.data as any).status;
          planDispatch({
            type: PlanActionType.UPDATE_PLAN_DRIVER_STATUS,
            payload: {
              driverId,
              status,
            },
          });

          // Driver is opened at right panel so refetch the driver data.
          if (detailId === driverId && detailType === `driver`) {
            refetchDriver();
          }
          break;
        }
        case `smartmonkey.planner.2.drivers.route.tracked`: {
          const { id: driverId, last_position } = pubResponse.data as any;
          const lastPosition = last_position ? GPSTrackMapper.toDomain(last_position as GPSTrackDTO) : undefined;
          planDispatch({
            type: PlanActionType.UPDATE_PLAN_DRIVER_LAST_POSITION,
            payload: {
              driverId,
              lastPosition,
            },
          });

          // Driver is opened at right panel so refetch the driver data.
          if (detailId === driverId && detailType === `driver`) {
            refetchDriver();
          }
          break;
        }
        default:
          break;
      }
    });

    return () => {
      if (activePlan) {
        webSocketsService.unsubscribe(getSubscriptionPlanPath(activePlan.id));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlan, detailId, detailType, detailTab]);
};
